import { useEffect, useState } from "react";
import { animate, motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  BarChart3,
  Bot,
  BrainCircuit,
  Command,
  GitBranch,
  Play,
  Send,
  Sparkles,
  Workflow,
  Zap,
} from "lucide-react";

const promptPresets = [
  "Build AI chatbot",
  "Automate my workflow",
  "Launch predictive analytics",
];

const heroStats = [
  {
    end: 80,
    label: "manual work reduced",
    formatter: (value) => `${Math.round(value)}%`,
  },
  {
    end: 10,
    label: "automated workflows",
    formatter: (value) => `${Math.max(1, Math.round(value))}K+`,
  },
  {
    end: 500,
    label: "AI deployments",
    formatter: (value) => `${Math.round(value)}+`,
  },
];

const heroHighlights = [
  "Automation",
  "Chatbots",
  "Analytics",
  "Integration",
];

const workflowPhases = [
  {
    title: "Input",
    description: "Prompts, forms, events, and files stream into a single control layer.",
    color: "#22D3EE",
  },
  {
    title: "AI Analysis",
    description: "Neural models classify, summarize, and choose the best automation path.",
    color: "#6366F1",
  },
  {
    title: "Automation Engine",
    description: "Logic, approvals, integrations, and AI agents execute in parallel.",
    color: "#A78BFA",
  },
  {
    title: "Smart Output",
    description: "Actions, dashboards, alerts, and copilots deliver polished outcomes.",
    color: "#60A5FA",
  },
];

const promptProfiles = {
  workflow: {
    title: "Workflow autopilot",
    summary:
      "Turn manual approvals, notifications, and handoffs into an event-driven AI automation sequence.",
    output: "Ops orchestration pack",
    tags: ["Routing logic", "Human approvals", "Live sync"],
  },
  chatbot: {
    title: "Conversational AI launch",
    summary:
      "Assemble a grounded chatbot with retrieval, escalation rules, and omnichannel response handling.",
    output: "Chatbot deployment map",
    tags: ["Knowledge search", "Agent handoff", "Voice consistency"],
  },
  analytics: {
    title: "Predictive intelligence layer",
    summary:
      "Connect business signals to forecasting models that score, prioritize, and trigger next-best actions.",
    output: "Forecast activation plan",
    tags: ["Trend scoring", "Anomaly alerts", "Decisioning"],
  },
  default: {
    title: "Adaptive AI automation",
    summary:
      "Blend AI analysis, orchestration, and workflow integration into a single modern automation stack.",
    output: "Custom automation blueprint",
    tags: ["Rapid prototyping", "Secure deployment", "Model orchestration"],
  },
};

const particleDots = Array.from({ length: 38 }, (_, index) => ({
  id: index,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  size: 2 + Math.random() * 3,
  delay: Math.random() * 8,
  duration: 8 + Math.random() * 8,
  color:
    index % 3 === 0
      ? "rgba(34, 211, 238, 0.6)"
      : index % 3 === 1
      ? "rgba(99, 102, 241, 0.55)"
      : "rgba(167, 139, 250, 0.5)",
}));

const neuralNodes = [
  { id: "n1", x: 92, y: 114, size: 6, color: "#22D3EE" },
  { id: "n2", x: 142, y: 208, size: 5, color: "#60A5FA" },
  { id: "n3", x: 124, y: 332, size: 6, color: "#A78BFA" },
  { id: "n4", x: 206, y: 136, size: 7, color: "#818CF8" },
  { id: "n5", x: 258, y: 250, size: 11, color: "#A78BFA" },
  { id: "n6", x: 218, y: 372, size: 7, color: "#22D3EE" },
  { id: "n7", x: 334, y: 116, size: 7, color: "#60A5FA" },
  { id: "n8", x: 370, y: 220, size: 6, color: "#22D3EE" },
  { id: "n9", x: 334, y: 336, size: 7, color: "#818CF8" },
  { id: "n10", x: 434, y: 174, size: 6, color: "#22D3EE" },
  { id: "n11", x: 430, y: 308, size: 8, color: "#A78BFA" },
];

const neuralLookup = Object.fromEntries(
  neuralNodes.map((node) => [node.id, node])
);

const neuralLinks = [
  ["n1", "n4"],
  ["n1", "n2"],
  ["n2", "n5"],
  ["n2", "n3"],
  ["n3", "n6"],
  ["n4", "n5"],
  ["n4", "n7"],
  ["n5", "n6"],
  ["n5", "n8"],
  ["n5", "n9"],
  ["n7", "n8"],
  ["n8", "n10"],
  ["n8", "n11"],
  ["n9", "n11"],
  ["n7", "n10"],
  ["n6", "n9"],
];

const orbitNodeCount = 12;

const orbitalNeuralNodes = Array.from({ length: orbitNodeCount }, (_, index) => {
  const angle = (index / orbitNodeCount) * Math.PI * 2 - Math.PI / 2;
  const radius = index % 2 === 0 ? 168 : 142;

  return {
    id: `orbit-${index + 1}`,
    x: 260 + Math.cos(angle) * radius,
    y: 260 + Math.sin(angle) * radius,
    size: index % 2 === 0 ? 4.2 : 3.5,
    color: index % 2 === 0 ? "#22D3EE" : "#A78BFA",
  };
});

const orbitalLookup = Object.fromEntries(
  orbitalNeuralNodes.map((node) => [node.id, node])
);

const orbitalRingLinks = orbitalNeuralNodes.map((node, index) => [
  node.id,
  orbitalNeuralNodes[(index + 1) % orbitalNeuralNodes.length].id,
]);

const orbitalChordLinks = orbitalNeuralNodes.map((node, index) => [
  node.id,
  orbitalNeuralNodes[(index + 4) % orbitalNeuralNodes.length].id,
]);

const floatingTools = [
  {
    icon: Workflow,
    label: "Automation",
    color: "#22D3EE",
    className: "top-8 -left-2 xl:-left-8",
  },
  {
    icon: Bot,
    label: "Chatbot",
    color: "#818CF8",
    className: "top-16 right-2 xl:-right-6",
  },
  {
    icon: BarChart3,
    label: "Analytics",
    color: "#A78BFA",
    className: "bottom-24 -left-3 xl:-left-7",
  },
  {
    icon: GitBranch,
    label: "Integration",
    color: "#60A5FA",
    className: "bottom-12 right-6 xl:right-0",
  },
];

const orbitIcons = [
  { icon: Bot, color: "#22D3EE", angle: 0 },
  { icon: BrainCircuit, color: "#818CF8", angle: 45 },
  { icon: Command, color: "#60A5FA", angle: 90 },
  { icon: Sparkles, color: "#A78BFA", angle: 135 },
  { icon: Bot, color: "#22D3EE", angle: 180 },
  { icon: BrainCircuit, color: "#A78BFA", angle: 225 },
  { icon: Workflow, color: "#60A5FA", angle: 270 },
  { icon: Command, color: "#818CF8", angle: 315 },
];

const selectPromptProfile = (prompt) => {
  const normalizedPrompt = prompt.toLowerCase();

  if (normalizedPrompt.includes("chatbot")) {
    return promptProfiles.chatbot;
  }

  if (
    normalizedPrompt.includes("predict") ||
    normalizedPrompt.includes("analytics")
  ) {
    return promptProfiles.analytics;
  }

  if (
    normalizedPrompt.includes("workflow") ||
    normalizedPrompt.includes("automate")
  ) {
    return promptProfiles.workflow;
  }

  return promptProfiles.default;
};

const AnimatedCounter = ({ end, formatter, duration = 2.3 }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const controls = animate(0, end, {
      duration,
      ease: "easeOut",
      onUpdate: (latest) => setValue(latest),
    });

    return () => controls.stop();
  }, [duration, end]);

  return <span>{formatter(value)}</span>;
};

const Particles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particleDots.map((dot) => (
        <div
          key={dot.id}
          className="absolute rounded-full"
          style={{
            left: dot.left,
            top: dot.top,
            width: dot.size,
            height: dot.size,
            background: dot.color,
            boxShadow: `0 0 ${dot.size * 5}px ${dot.color}`,
            animation: `particle-float ${dot.duration}s ease-in-out ${dot.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
};

const NeuralNetworkVisual = ({ activePrompt, activePhase }) => {
  const promptProfile = selectPromptProfile(activePrompt);

  return (
    <div className="relative mx-auto w-full max-w-[470px] lg:ml-auto lg:-mt-6 xl:-mt-8">
      <div className="absolute inset-0 translate-y-6 scale-[1.05] rounded-[36px] bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.28),transparent_62%)] blur-3xl opacity-70" />

      {floatingTools.map((tool, index) => (
        <motion.div
          key={tool.label}
          animate={{ y: [0, -12, 0] }}
          transition={{
            duration: 5.5 + index * 0.7,
            repeat: Infinity,
            ease: "easeInOut",
            delay: index * 0.6,
          }}
          className={`absolute z-20 hidden rounded-2xl border border-white/10 bg-slate-950/68 px-3 py-2 shadow-[0_18px_60px_rgba(15,23,42,0.45)] backdrop-blur-2xl lg:block ${tool.className}`}
        >
          <div className="flex items-center gap-3">
            <div
                className="flex h-[2.125rem] w-[2.125rem] items-center justify-center rounded-2xl"
              style={{
                background: `${tool.color}18`,
                border: `1px solid ${tool.color}40`,
                boxShadow: `0 0 20px ${tool.color}20`,
              }}
            >
              <tool.icon
                className="h-3.5 w-3.5"
                style={{
                  color: tool.color,
                  filter: `drop-shadow(0 0 8px ${tool.color})`,
                }}
              />
            </div>
            <div>
              <p className="text-[8px] uppercase tracking-[0.3em] text-slate-500">
                AI Tool
              </p>
              <p className="mt-0.5 text-[0.86rem] font-medium text-white">{tool.label}</p>
            </div>
          </div>
        </motion.div>
      ))}

      <motion.div
        initial={{ opacity: 0, x: 40, scale: 0.96 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-[30px] border border-white/10 bg-[linear-gradient(155deg,rgba(8,13,28,0.94),rgba(17,24,43,0.82))] p-3.5 shadow-[0_35px_120px_rgba(2,6,23,0.55)] sm:p-[1.125rem]"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.16),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(167,139,250,0.22),transparent_34%)]" />
        <div className="absolute inset-0 tech-grid opacity-30" />
        <div className="absolute inset-0 scan-line opacity-35" />

        <div className="relative z-10 flex flex-wrap items-center justify-between gap-2.5">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-cyan-200/70">
              Neural Network
            </p>
            <h2
              className="mt-1 text-[1.45rem] font-semibold text-white sm:text-[1.65rem]"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              Live automation mesh
            </h2>
          </div>
          <span className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-[10px] font-medium tracking-[0.2em] text-cyan-200 uppercase">
            {promptProfile.output}
          </span>
        </div>

        <div className="relative mt-3 h-[15.75rem] sm:h-[16.5rem] lg:h-[15.25rem] xl:h-[16rem]">
          {[76, 138, 196].map((size, index) => (
            <div
              key={size}
              className="absolute left-1/2 top-1/2 rounded-full border border-white/6"
              style={{
                width: size * 2,
                height: size * 2,
                marginLeft: -size,
                marginTop: -size,
                animation: `glow-pulse-ring ${6 + index * 1.4}s ease-in-out ${index * 0.4}s infinite`,
              }}
            />
          ))}

          <motion.div
            className="pointer-events-none absolute inset-0 z-20"
            animate={{ rotate: 360 }}
            transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: "50% 50%" }}
          >
            {orbitIcons.map((item) => {
              const IconComponent = item.icon;

              return (
                <motion.div
                  key={`${item.angle}-${item.color}`}
                  className="absolute left-1/2 top-1/2"
                  style={{ transform: `rotate(${item.angle}deg) translateY(-7.15rem)` }}
                  animate={{ rotate: -360 }}
                  transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
                >
                  <div
                    className="flex h-6 w-6 items-center justify-center rounded-full border bg-slate-950/78 backdrop-blur"
                    style={{
                      borderColor: `${item.color}50`,
                      boxShadow: `0 0 14px ${item.color}30`,
                    }}
                  >
                    <IconComponent
                      className="h-3 w-3"
                      style={{
                        color: item.color,
                        filter: `drop-shadow(0 0 7px ${item.color})`,
                      }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          <svg viewBox="0 0 520 520" className="relative z-10 h-full w-full">
            <motion.g
              animate={{ rotate: 360 }}
              transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
              style={{ transformOrigin: "260px 260px" }}
            >
              {orbitalRingLinks.map((link, index) => {
                const from = orbitalLookup[link[0]];
                const to = orbitalLookup[link[1]];

                return (
                  <line
                    key={`ring-${link[0]}-${link[1]}`}
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    stroke="rgba(125, 211, 252, 0.2)"
                    strokeWidth="1.1"
                    strokeDasharray={index % 2 === 0 ? "4 10" : "5 9"}
                  />
                );
              })}

              {orbitalChordLinks.map((link) => {
                const from = orbitalLookup[link[0]];
                const to = orbitalLookup[link[1]];

                return (
                  <line
                    key={`chord-${link[0]}-${link[1]}`}
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    stroke="rgba(196, 181, 253, 0.16)"
                    strokeWidth="0.95"
                  />
                );
              })}

              {orbitalNeuralNodes.map((node, index) => (
                <g key={node.id}>
                  <circle cx={node.x} cy={node.y} r={node.size * 2.3} fill={node.color} fillOpacity="0.12" />
                  <motion.circle
                    cx={node.x}
                    cy={node.y}
                    r={node.size}
                    fill={node.color}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{
                      duration: 2.2 + (index % 4) * 0.35,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </g>
              ))}
            </motion.g>

            {neuralLinks.map((link, index) => {
              const from = neuralLookup[link[0]];
              const to = neuralLookup[link[1]];
              const activeColor =
                index % 2 === 0 ? "rgba(34, 211, 238, 0.55)" : "rgba(167, 139, 250, 0.45)";
              const restingOpacity = index % workflowPhases.length === activePhase ? 0.95 : 0.42;

              return (
                <motion.line
                  key={`${link[0]}-${link[1]}`}
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke={activeColor}
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeDasharray="11 12"
                  animate={{
                    strokeDashoffset: [24, 0],
                    opacity: [restingOpacity * 0.65, restingOpacity, restingOpacity * 0.65],
                  }}
                  transition={{
                    duration: 3.2 + (index % 3) * 0.6,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
              );
            })}

            {neuralNodes.map((node, index) => (
              <g key={node.id}>
                <motion.circle
                  cx={node.x}
                  cy={node.y}
                  r={node.size * 2.7}
                  fill={node.color}
                  animate={{ opacity: [0.08, 0.24, 0.08] }}
                  transition={{
                    duration: 3 + (index % 4) * 0.6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <motion.circle
                  cx={node.x}
                  cy={node.y}
                  r={node.size}
                  fill={node.color}
                  animate={{
                    opacity: [0.75, 1, 0.75],
                    filter: [
                      "drop-shadow(0 0 6px rgba(34,211,238,0.35))",
                      "drop-shadow(0 0 14px rgba(34,211,238,0.65))",
                      "drop-shadow(0 0 6px rgba(34,211,238,0.35))",
                    ],
                  }}
                  transition={{
                    duration: 2.5 + (index % 5) * 0.4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </g>
            ))}
          </svg>

          <div className="absolute left-1/2 top-1/2 z-20 flex h-[6rem] w-[6rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-violet-300/18 bg-violet-300/10 shadow-[0_0_80px_rgba(139,92,246,0.28)] backdrop-blur-xl">
            <div className="flex h-[3.85rem] w-[3.85rem] items-center justify-center rounded-full border border-cyan-300/18 bg-slate-950/85">
              <BrainCircuit className="h-7 w-7 text-cyan-300 drop-shadow-[0_0_16px_rgba(34,211,238,0.7)]" />
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-3 rounded-[22px] border border-white/10 bg-slate-950/60 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:p-3.5">
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="max-w-[16.5rem]">
                <p className="text-[11px] uppercase tracking-[0.32em] text-slate-500">
                  Current Command
                </p>
                <p className="mt-1 text-sm text-white sm:text-[0.92rem]">{activePrompt}</p>
                <p className="mt-1 text-[0.8rem] leading-5 text-slate-400">
                  {promptProfile.summary}
                </p>
              </div>
              <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-medium tracking-[0.22em] text-slate-300 uppercase">
                {workflowPhases[activePhase].title}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {promptProfile.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-medium tracking-[0.16em] text-slate-300 uppercase"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-2.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {workflowPhases.map((phase, index) => {
              const isActive = index === activePhase;

              return (
                <div
                  key={phase.title}
                  className="rounded-2xl border px-2.5 py-2 text-left transition-all duration-300"
                  style={{
                    background: isActive ? `${phase.color}18` : "rgba(255, 255, 255, 0.03)",
                    borderColor: isActive ? `${phase.color}55` : "rgba(255, 255, 255, 0.08)",
                    boxShadow: isActive ? `0 0 22px ${phase.color}18` : "none",
                  }}
                >
                  <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    {phase.title}
                  </p>
                  <p className="mt-1 text-[0.88rem] font-medium text-white">
                    {index + 1 < 10 ? `0${index + 1}` : index + 1}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const HeroSection = () => {
  const [commandInput, setCommandInput] = useState(promptPresets[0]);
  const [activePrompt, setActivePrompt] = useState(promptPresets[0]);
  const [activePhase, setActivePhase] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActivePhase((previousPhase) => (previousPhase + 1) % workflowPhases.length);
    }, 1500);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }

    const timer = window.setTimeout(() => setIsRunning(false), 1400);
    return () => window.clearTimeout(timer);
  }, [isRunning]);

  const handlePresetClick = (prompt) => {
    setCommandInput(prompt);
    setActivePrompt(prompt);
    setIsRunning(true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextPrompt = commandInput.trim() || promptPresets[0];
    setCommandInput(nextPrompt);
    setActivePrompt(nextPrompt);
    setIsRunning(true);
  };

  return (
    <section className="relative isolate overflow-hidden bg-primary pb-14 pt-[6.5rem] sm:pb-[4.5rem] sm:pt-[7.5rem] lg:min-h-screen lg:pb-14 lg:pt-[6.8rem]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.14),transparent_28%),radial-gradient(circle_at_80%_18%,rgba(139,92,246,0.16),transparent_22%),linear-gradient(140deg,#040816_0%,#0A1021_38%,#111936_68%,#070B16_100%)]" />
      <div className="absolute inset-0 tech-grid opacity-55" />
      <div className="absolute inset-0 scan-line opacity-30" />
      <Particles />

      <div className="absolute left-[-10%] top-[10%] h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(34,211,238,0.18),transparent_68%)] blur-[120px]" />
      <div className="absolute bottom-[-12%] right-[-5%] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(167,139,250,0.2),transparent_66%)] blur-[130px]" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.98fr_1.02fr] lg:items-start lg:px-8 xl:gap-10">
        <div className="max-w-[37rem]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="ai-badge mb-5 inline-flex"
          >
            <Sparkles className="h-3.5 w-3.5 text-ai-glow" />
            Autonomous AI Control Room
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.75 }}
            className="max-w-4xl text-[2.75rem] font-bold leading-[0.9] text-white sm:text-[3.55rem] lg:text-[3.7rem] xl:text-[4.15rem]"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            Automate Everything
            <br />
            With <span className="gradient-text-glow">Neural AI</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-4 max-w-xl text-[0.95rem] leading-6 text-slate-300 sm:text-[1rem]"
          >
            Build AI chatbots, automate workflows, connect your stack, and launch
            predictive systems from a clean neural command center.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.26, duration: 0.55 }}
            className="mt-4 flex flex-wrap gap-2"
          >
            {heroHighlights.map((item) => (
              <span
                key={item}
                className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[11px] font-medium tracking-[0.22em] text-slate-300 uppercase"
              >
                {item}
              </span>
            ))}
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            onSubmit={handleSubmit}
            className="relative mt-5 overflow-hidden rounded-[28px] border border-white/12 bg-[linear-gradient(160deg,rgba(8,12,28,0.92),rgba(18,25,48,0.82))] p-1 shadow-[0_24px_90px_rgba(2,6,23,0.5)]"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.12),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(139,92,246,0.18),transparent_34%)]" />
            <div className="relative rounded-[26px] border border-white/8 bg-slate-950/65 p-3.5 backdrop-blur-2xl">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-300/25 bg-cyan-400/10">
                    <Command className="h-5 w-5 text-cyan-300" />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.35em] text-slate-500">
                      AI Command Input
                    </p>
                    <p className="mt-1 text-sm text-slate-300">
                      Type a prompt to simulate your next automation build.
                    </p>
                  </div>
                </div>
                <div className="rounded-full border border-emerald-400/20 bg-emerald-400/8 px-4 py-2 text-xs font-medium tracking-[0.26em] text-emerald-300 uppercase">
                  {isRunning ? "Analyzing prompt" : "System ready"}
                </div>
              </div>

              <div className="mt-3.5 flex flex-col gap-3 lg:flex-row">
                <div className="relative flex-1">
                  <Sparkles className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-cyan-300/80" />
                  <input
                    type="text"
                    value={commandInput}
                    onChange={(event) => setCommandInput(event.target.value)}
                    className="h-[3.35rem] w-full rounded-2xl border border-white/10 bg-white/5 pl-[3rem] pr-4 text-[0.95rem] text-white placeholder:text-slate-500 focus:border-cyan-300/40 focus:outline-none focus:ring-0"
                    placeholder='Try "Automate my workflow" or "Build AI chatbot"'
                    aria-label="AI command input"
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex h-[3.35rem] items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#22D3EE_0%,#6366F1_45%,#A78BFA_100%)] px-6 text-sm font-semibold text-slate-950 shadow-[0_0_30px_rgba(34,211,238,0.35)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_44px_rgba(99,102,241,0.45)]"
                >
                  {isRunning ? "Running" : "Run Simulation"}
                  <Send className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-3.5 flex flex-wrap gap-2">
                {promptPresets.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => handlePresetClick(prompt)}
                    className={`rounded-full border px-3.5 py-1.5 text-[0.84rem] transition-all duration-300 ${
                      activePrompt === prompt
                        ? "border-cyan-300/40 bg-cyan-400/10 text-cyan-200 shadow-[0_0_20px_rgba(34,211,238,0.14)]"
                        : "border-white/10 bg-white/5 text-slate-400 hover:border-white/20 hover:text-white"
                    }`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.7 }}
            className="mt-4 flex flex-wrap gap-3"
          >
            <Link to="/contact" className="neon-btn neon-btn-primary no-underline">
              <Zap className="h-5 w-5" />
              Build My AI System
            </Link>
            <Link to="/services" className="neon-btn neon-btn-outline no-underline">
              <Play className="h-5 w-5" />
              Explore Automation Stack
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-4 grid gap-2.5 sm:grid-cols-3"
          >
            {heroStats.map((stat, index) => (
              <div
                key={stat.label}
                className="rounded-[22px] border border-white/10 bg-[linear-gradient(160deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-3.5 shadow-[0_18px_40px_rgba(2,6,23,0.3)] backdrop-blur-xl"
                style={{
                  animation: `float-slow ${6 + index * 0.8}s ease-in-out ${index * 0.35}s infinite`,
                }}
              >
                <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
                  Neural impact
                </p>
                <div
                  className="mt-2 text-[1.9rem] font-bold text-white sm:text-[2rem]"
                  style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                >
                  <AnimatedCounter end={stat.end} formatter={stat.formatter} />
                </div>
                <p className="mt-1 text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <NeuralNetworkVisual activePrompt={activePrompt} activePhase={activePhase} />
      </div>

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-primary to-transparent" />
    </section>
  );
};

export default HeroSection;
